import { FC, KeyboardEvent } from 'react'

import { css, styled } from 'styled-components'

import { Field } from 'core/hooks/useForm'
import { error, focusRing, radiusInput, variables } from 'core/styles'

type TextareaProps = Field<string> & {
  className?: string
  /** The starting number of visible text lines. */
  rows?: number
  /** The maximum number of visible text lines. */
  maxRows?: number
  /** Clamps the text length. When set shows a character counter. */
  maxLength?: number
  /** When true the control grabs focus as soon as it's rendered. */
  autoFocus?: boolean
  /** A hint to the user of what can be entered in the control. */
  placeholder?: string
  /** Optional keydown handler for advanced behaviors (e.g., Tab indent) */
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void
}

/**
 * A `textarea`, with an optional character coutner, that grows to fit content.
 */
const Textarea: FC<TextareaProps> = ({
  className,
  disabled,
  maxLength,
  maxRows,
  name,
  onChange,
  onKeyDown,
  rows = 3,
  value,
  errorMessage,
  ...props
}) => (
  <>
    <Box $maxRows={maxRows} $rows={rows} className={className}>
      <StyledTextarea
        $errorMessage={!!errorMessage}
        disabled={disabled}
        maxLength={maxLength}
        name={name}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
        value={value}
        {...props}
      />

      <GrowBox>{value}&#8203;</GrowBox>
      {!!maxLength && value && value.length > 0 && (
        <Counter>
          {value.length}/{maxLength}
        </Counter>
      )}
    </Box>
    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
  </>
)

export default Textarea

export const Box = styled.div<{ $rows: number; $maxRows?: number }>`
  position: relative;
  width: 100%;
  min-height: ${(p) => p.$rows * 24 + 18}px;
  ${({ $rows, $maxRows }) =>
    !!$maxRows && $maxRows >= $rows
      ? `max-height: ${$maxRows * 24 + 18}px;`
      : ''}
  line-height: 1.5;
  font-size: 16px;
`

export const TextAreaCSS = css<{
  $errorMessage?: boolean
}>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${radiusInput};
  background-color: ${(p) => p.theme.background};
  padding: 8px;
  width: 100%;
  height: 100%;
  resize: none;
  line-height: inherit;
  color: ${(p) => p.theme.text};
  font-family: inherit;
  font-size: inherit;

  &:hover {
    border-color: ${variables.colorBlack30};
  }

  &:focus {
    outline: none;
    border: 1px solid ${(p) => p.theme.highlight};
    box-shadow: ${focusRing};
  }

  &:disabled {
    border-color: ${(p) => p.theme.border};
    background-color: ${(p) => p.theme.mutedText};
    color: ${(p) => p.theme.disabledText};
  }

  ::placeholder {
    color: ${variables.colorBlack50};
  }

  ${(p) =>
    p.$errorMessage &&
    `
      border-color: ${error} !important;
    `}
`

const StyledTextarea = styled.textarea<{
  $errorMessage?: boolean
}>`
  ${TextAreaCSS}
`

const GrowBox = styled.div`
  visibility: hidden;
  border: 1px solid transparent;
  padding: 8px;
  white-space: pre-wrap;
  font-family: inherit;
`

const Counter = styled.div`
  position: absolute;
  right: 8px;
  bottom: -11px;
  z-index: 1;
  background-color: ${(p) => p.theme.background};
  padding: 0 2px;
  color: ${(p) => p.theme.mutedText};
  font-size: 14px;
`

const ErrorMessage = styled.div`
  margin-top: 4px;
  color: ${error};
  font-size: 12px;
`
